import {
  type NotificationsQuery,
  useNotificationsQuery,
} from "@graphql/notifications/Notifications.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import { json2csv } from "json-2-csv";
import { useCallback, useMemo, useState } from "react";

import { queryClient } from "@providers/QueryClient/queryClient";
import { useTableColumnSettings } from "@providers/TableColumnSettingsProvider/useTableColumnSettings";

import { formatExactDateTime } from "@utils/datetime";
import {
  getNotificationReasonLabel,
  NO_EMPLOYEE_IDENTIFIED,
} from "@utils/notificationReasons";

import type { NotificationsViewMode } from "../../NotificationsTable/NotificationsTable";
import {
  CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS,
  ClientNotificationTableColumns,
  DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS,
} from "../UI/ClientNotificationsTableColumns";

type Props = {
  tableId: string;
  clientId: string;
  exportType?: "csv";
  viewMode?: NotificationsViewMode;
  notificationType?: NotificationEnum[];
  applicationNameSearch?: string;
  resolvedById?: string;
  resolvedAt_Gte?: string;
  resolvedAt_Lte?: string;
};

export const useClientNotificationTableExport = ({
  tableId,
  clientId,
  exportType = "csv",
  viewMode = "active",
  notificationType,
  applicationNameSearch,
  resolvedById,
  resolvedAt_Gte,
  resolvedAt_Lte,
}: Props) => {
  const [isExporting, setIsExporting] = useState(false);
  const isHistoryView = viewMode === "history";
  const { getColumnSettings } = useTableColumnSettings({
    tableId,
  });

  const exportData = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const BATCH_SIZE = 100;
      const allNotifications: NonNullable<
        NotificationsQuery["notifications"]
      >["edges"] = [];
      let hasNextPage = true;
      let offset = 0;

      const queryFilters = {
        clientId,
        first: BATCH_SIZE,
        resolvedAt_Isnull: !isHistoryView,
        notificationType_In:
          notificationType && notificationType.length > 0
            ? notificationType
            : undefined,
        application_Resource_ProductName_Icontains:
          applicationNameSearch && applicationNameSearch.trim()
            ? applicationNameSearch.trim()
            : undefined,
        ...(isHistoryView && resolvedById ? { resolvedById } : {}),
        ...(isHistoryView && resolvedAt_Gte ? { resolvedAt_Gte } : {}),
        ...(isHistoryView && resolvedAt_Lte ? { resolvedAt_Lte } : {}),
      };

      while (hasNextPage) {
        const data = await queryClient.fetchQuery({
          queryKey: useNotificationsQuery.getKey({
            ...queryFilters,
            offset,
          }),
          queryFn: useNotificationsQuery.fetcher({
            ...queryFilters,
            offset,
          }),
        });

        const edges = data.notifications?.edges || [];
        allNotifications.push(...edges);

        hasNextPage = edges.length === BATCH_SIZE;
        offset += BATCH_SIZE;
      }

      const columnSettings =
        getColumnSettings() ?? DEFAULT_CLIENT_NOTIFICATION_TABLE_COLUMN_SETTINGS;
      const selectedColumns = Object.entries(columnSettings)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => value)
        .map(([key]) => key as ClientNotificationTableColumns);

      const notifications = allNotifications
        .map((edge) => {
          if (!edge?.node) return null;

          const notification: Record<string, unknown> = {};

          selectedColumns.forEach((column) => {
            const label = CLIENT_NOTIFICATION_TABLE_COLUMN_LABELS[column];

            switch (column) {
              case ClientNotificationTableColumns.applicationName:
                notification[label] =
                  edge.node?.application?.resource.productName ||
                  "Unknown Notification";
                break;
              case ClientNotificationTableColumns.employee:
                notification[label] = edge.node?.employee
                  ? [edge.node.employee.name, edge.node.employee.email]
                      .filter(Boolean)
                      .join(" ")
                  : NO_EMPLOYEE_IDENTIFIED;
                break;
              case ClientNotificationTableColumns.reason:
                notification[label] = getNotificationReasonLabel(
                  edge.node?.reason,
                  edge.node?.notificationType,
                );
                break;
              case ClientNotificationTableColumns.notificationType:
                notification[label] = edge.node?.notificationType || "";
                break;
              case ClientNotificationTableColumns.createdAt:
                notification[label] =
                  formatExactDateTime(edge.node?.createdAt) ?? "";
                break;
              case ClientNotificationTableColumns.resolvedBy:
                notification[label] =
                  edge.node?.resolvedBy?.name ||
                  edge.node?.resolvedBy?.email ||
                  "";
                break;
              case ClientNotificationTableColumns.resolvedAt:
                notification[label] =
                  formatExactDateTime(edge.node?.resolvedAt) ?? "";
                break;
              default:
                notification[label] = "";
            }
          });

          return notification;
        })
        .filter(
          (notification): notification is Record<string, unknown> =>
            notification !== null,
        );

      if (!notifications || notifications.length === 0) {
        throw new Error("No notifications found");
      }

      if (exportType === "csv") {
        const csv = json2csv(notifications);
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = isHistoryView
          ? `client-notification-history-${new Date().toISOString().split("T")[0]}.csv`
          : `client-notifications-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export failed:", error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, [
    exportType,
    getColumnSettings,
    clientId,
    isHistoryView,
    notificationType,
    applicationNameSearch,
    resolvedById,
    resolvedAt_Gte,
    resolvedAt_Lte,
    isExporting,
  ]);

  return useMemo(() => ({ exportData, isExporting }), [exportData, isExporting]);
};
